/**
 * Aerospace Industry Configuration
 *
 * Supports: Engines, Landing Gear, Avionics, Hydraulic Systems, Fuel Systems, APU
 */

import { IndustryConfig } from '../industryTypes'

export const aerospaceConfig: IndustryConfig = {
  id: 'aerospace',
  name: 'Aerospace',
  description: 'Aircraft systems monitoring and MRO operations',
  icon: 'Plane',

  terminology: {
    // Asset terminology
    asset: 'Aircraft System',
    assets: 'Aircraft Systems',
    assetPlural: 'Aircraft Systems',
    assetType: 'System Type',
    // Health and status terminology
    failure: 'Malfunction',
    health: 'Airworthiness',
    healthScore: 'Airworthiness Score',
    healthy: 'Serviceable',
    degraded: 'Deferred',
    critical: 'AOG (Aircraft on Ground)',
    alert: 'PIREP',
    // Operations terminology
    process: 'Operations',
    maintenance: 'MRO',
    inspection: 'Inspection',
    downtime: 'Out of Service',
    workOrder: 'Work Package',
    // Analytics terminology
    prediction: 'Prognostics',
    anomaly: 'Fault',
    trend: 'Trend',
    risk: 'Safety Risk',
    // Facility terminology
    facility: 'Hangar',
    facilityPlural: 'Hangars',
    location: 'Bay',
    // Personnel terminology
    operator: 'A&P Mechanic',
    supervisor: 'Lead Mechanic',
    // Compliance terminology
    calibration: 'Calibration',
    compliance: 'Airworthiness Directive Compliance',
    criticality: 'Dispatch Criticality',
  },

  assetTypes: [
    {
      id: 'engine',
      name: 'Engine',
      icon: 'Cog',
      description: 'Turbofan, turboprop, and turboshaft propulsion systems',
      category: 'Propulsion',
      defaultMetrics: ['egt', 'n1_speed', 'n2_speed', 'oil_pressure', 'fuel_flow', 'vibration'],
      maintenanceTypes: ['borescope_inspection', 'oil_analysis', 'module_replacement', 'overhaul'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Flight safety critical', downtimeCostMultiplier: 50 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Dispatch reliability impact', downtimeCostMultiplier: 25 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Performance impact', downtimeCostMultiplier: 10 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Minor impact', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'landing_gear',
      name: 'Landing Gear',
      icon: 'CircleDot',
      description: 'Main and nose landing gear assemblies',
      category: 'Structures',
      defaultMetrics: ['strut_pressure', 'tire_pressure', 'brake_wear', 'actuator_position', 'hydraulic_pressure'],
      maintenanceTypes: ['tire_change', 'brake_replacement', 'strut_service', 'overhaul'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Flight safety critical', downtimeCostMultiplier: 40 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Ground operations impact', downtimeCostMultiplier: 20 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Wear monitoring', downtimeCostMultiplier: 8 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Cosmetic/minor', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'avionics',
      name: 'Avionics',
      icon: 'Radio',
      description: 'Flight management, navigation, and communication systems',
      category: 'Electronics',
      defaultMetrics: ['bit_status', 'signal_strength', 'database_currency', 'software_version', 'temperature'],
      maintenanceTypes: ['software_update', 'module_replacement', 'calibration', 'database_update'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Required for dispatch', downtimeCostMultiplier: 35 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'MEL item', downtimeCostMultiplier: 15 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Redundant system available', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Non-essential function', downtimeCostMultiplier: 1 },
      ],
    },
    {
      id: 'hydraulic_system',
      name: 'Hydraulic System',
      icon: 'Droplets',
      description: 'Flight controls, landing gear, and utility hydraulic systems',
      category: 'Systems',
      defaultMetrics: ['system_pressure', 'fluid_level', 'fluid_temperature', 'pump_output', 'filter_status'],
      maintenanceTypes: ['fluid_service', 'filter_change', 'pump_replacement', 'seal_replacement'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Primary flight controls', downtimeCostMultiplier: 45 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Secondary flight controls', downtimeCostMultiplier: 20 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Utility functions', downtimeCostMultiplier: 8 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Backup system', downtimeCostMultiplier: 3 },
      ],
    },
    {
      id: 'fuel_system',
      name: 'Fuel System',
      icon: 'Fuel',
      description: 'Fuel storage, transfer, and delivery systems',
      category: 'Systems',
      defaultMetrics: ['fuel_quantity', 'fuel_flow', 'fuel_temperature', 'filter_pressure_drop', 'pump_status'],
      maintenanceTypes: ['filter_change', 'tank_inspection', 'pump_replacement', 'leak_check'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'Engine feed system', downtimeCostMultiplier: 40 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Transfer system', downtimeCostMultiplier: 18 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Quantity indication', downtimeCostMultiplier: 6 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Vent system', downtimeCostMultiplier: 2 },
      ],
    },
    {
      id: 'apu',
      name: 'APU',
      icon: 'Power',
      description: 'Auxiliary Power Unit for ground and emergency power',
      category: 'Propulsion',
      defaultMetrics: ['egt', 'oil_pressure', 'oil_temperature', 'generator_output', 'bleed_pressure'],
      maintenanceTypes: ['oil_service', 'filter_change', 'borescope_inspection', 'overhaul'],
      criticalityLevels: [
        { id: 'critical', name: 'Critical', color: '#ef4444', description: 'ETOPS required', downtimeCostMultiplier: 30 },
        { id: 'high', name: 'High', color: '#f59e0b', description: 'Ground operations essential', downtimeCostMultiplier: 15 },
        { id: 'medium', name: 'Medium', color: '#3b82f6', description: 'Convenience item', downtimeCostMultiplier: 5 },
        { id: 'low', name: 'Low', color: '#6b7280', description: 'Deferrable', downtimeCostMultiplier: 2 },
      ],
    },
  ],

  kpiDomains: [
    {
      id: 'airworthiness',
      name: 'Airworthiness',
      icon: 'ShieldCheck',
      description: 'Aircraft airworthiness and safety compliance metrics',
      businessValue: 'Ensure aircraft meet regulatory requirements for safe flight operations',
      category: 'universal',
      metrics: [
        { id: 'mel_items', name: 'MEL Items Open', unit: 'count', description: 'Open Minimum Equipment List deferrals', dataType: 'discrete', aggregation: 'count', goodDirection: 'lower', defaultThresholds: { healthy: 2, warning: 5, critical: 10 } },
        { id: 'ad_compliance', name: 'AD Compliance', unit: '%', description: 'Airworthiness Directive compliance rate', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 98, critical: 95 } },
        { id: 'inspection_current', name: 'Inspection Currency', unit: '%', description: 'Required inspections completed on time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 95, critical: 90 } },
        { id: 'documentation_complete', name: 'Documentation Complete', unit: '%', description: 'Required maintenance records complete', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 100, warning: 98, critical: 95 } },
      ],
      thresholds: { healthy: { min: 98 }, warning: { min: 90, max: 98 }, critical: { max: 90 } },
      visualizations: ['gauge', 'line_chart', 'comparison', 'trend'],
    },
    {
      id: 'dispatch_reliability',
      name: 'Dispatch Reliability',
      icon: 'Clock',
      description: 'Aircraft dispatch and operational reliability metrics',
      businessValue: 'Maximize revenue by ensuring aircraft availability for scheduled operations',
      category: 'universal',
      metrics: [
        { id: 'dispatch_rate', name: 'Dispatch Reliability', unit: '%', description: 'Percentage of flights dispatched on time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 99, warning: 97, critical: 95 } },
        { id: 'aog_events', name: 'AOG Events', unit: 'count', description: 'Aircraft on Ground events per month', dataType: 'discrete', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 1, warning: 3, critical: 5 } },
        { id: 'delay_minutes', name: 'Technical Delay Minutes', unit: 'min', description: 'Total technical delay minutes per month', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 60, warning: 180, critical: 360 } },
        { id: 'cancellation_rate', name: 'Technical Cancellation Rate', unit: '%', description: 'Flights cancelled due to technical issues', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 0.1, warning: 0.5, critical: 1.0 } },
      ],
      thresholds: { healthy: { min: 98 }, warning: { min: 95, max: 98 }, critical: { max: 95 } },
      visualizations: ['line_chart', 'bar_chart', 'trend', 'comparison'],
    },
    {
      id: 'maintenance_efficiency',
      name: 'Maintenance Efficiency',
      icon: 'Wrench',
      description: 'MRO operational efficiency and productivity metrics',
      businessValue: 'Optimize maintenance operations to reduce turnaround time and costs',
      category: 'industry_specific',
      metrics: [
        { id: 'tat', name: 'Turn Around Time', unit: 'hours', description: 'Average aircraft turnaround time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 2, warning: 4, critical: 8 } },
        { id: 'labor_utilization', name: 'Labor Utilization', unit: '%', description: 'Mechanic productive time percentage', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 85, warning: 70, critical: 55 } },
        { id: 'scheduled_compliance', name: 'Scheduled Maintenance Compliance', unit: '%', description: 'Scheduled tasks completed on time', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 85, critical: 75 } },
        { id: 'first_time_fix', name: 'First Time Fix Rate', unit: '%', description: 'Defects resolved without repeat', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 95, warning: 85, critical: 75 } },
      ],
      thresholds: { healthy: { min: 90 }, warning: { min: 75, max: 90 }, critical: { max: 75 } },
      visualizations: ['gauge', 'line_chart', 'bar_chart', 'trend'],
    },
    {
      id: 'cost_management',
      name: 'Cost Management',
      icon: 'DollarSign',
      description: 'Maintenance cost control and budget metrics',
      businessValue: 'Control maintenance costs while maintaining safety and reliability',
      category: 'universal',
      metrics: [
        { id: 'cost_per_flight_hour', name: 'Cost per Flight Hour', unit: '$/FH', description: 'Maintenance cost per flight hour', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 800, warning: 1000, critical: 1200 } },
        { id: 'spare_parts_cost', name: 'Spare Parts Cost', unit: '$K', description: 'Monthly spare parts expenditure', dataType: 'continuous', aggregation: 'sum', goodDirection: 'lower', defaultThresholds: { healthy: 100, warning: 150, critical: 200 } },
        { id: 'unscheduled_cost_ratio', name: 'Unscheduled Cost Ratio', unit: '%', description: 'Unscheduled vs total maintenance cost', dataType: 'continuous', aggregation: 'avg', goodDirection: 'lower', defaultThresholds: { healthy: 20, warning: 35, critical: 50 } },
        { id: 'warranty_recovery', name: 'Warranty Recovery', unit: '%', description: 'Warranty claims successfully recovered', dataType: 'continuous', aggregation: 'avg', goodDirection: 'higher', defaultThresholds: { healthy: 85, warning: 70, critical: 55 } },
      ],
      thresholds: { healthy: { min: 85 }, warning: { min: 70, max: 85 }, critical: { max: 70 } },
      visualizations: ['bar_chart', 'line_chart', 'comparison', 'trend'],
    },
  ],

  maintenanceTypes: [
    {
      id: 'line_maintenance',
      name: 'Line Maintenance',
      description: 'Routine checks and minor repairs between flights',
      category: 'preventive',
      defaultInterval: { value: 1, unit: 'days' },
      laborRequirements: [
        { role: 'A&P Mechanic', count: 2, hourlyRate: 65 },
      ],
      estimatedDuration: { min: 1, typical: 2, max: 4, unit: 'hours' },
      skillsRequired: ['general_airframe', 'powerplant', 'avionics_basic'],
      partsCommonly: ['filters', 'fluids', 'lights', 'tires'],
    },
    {
      id: 'base_maintenance',
      name: 'Base Maintenance',
      description: 'A and B checks performed at maintenance base',
      category: 'preventive',
      defaultInterval: { value: 500, unit: 'hours' },
      laborRequirements: [
        { role: 'A&P Mechanic', count: 6, hourlyRate: 65 },
        { role: 'Avionics Technician', count: 2, hourlyRate: 75 },
        { role: 'Inspector', count: 1, hourlyRate: 80 },
      ],
      estimatedDuration: { min: 24, typical: 48, max: 72, unit: 'hours' },
      skillsRequired: ['airframe', 'powerplant', 'avionics', 'structures', 'ndt'],
      partsCommonly: ['filters', 'seals', 'bearings', 'actuators', 'avionics_lrus'],
    },
    {
      id: 'heavy_check',
      name: 'Heavy Check (C/D Check)',
      description: 'Major scheduled maintenance requiring extended downtime',
      category: 'preventive',
      defaultInterval: { value: 18, unit: 'months' },
      laborRequirements: [
        { role: 'A&P Mechanic', count: 20, hourlyRate: 65 },
        { role: 'Avionics Technician', count: 6, hourlyRate: 75 },
        { role: 'Structures Specialist', count: 4, hourlyRate: 85 },
        { role: 'Inspector', count: 3, hourlyRate: 80 },
        { role: 'Engineer', count: 2, hourlyRate: 100 },
      ],
      estimatedDuration: { min: 14, typical: 21, max: 35, unit: 'days' },
      skillsRequired: ['airframe', 'powerplant', 'avionics', 'structures', 'composites', 'ndt', 'paint'],
      partsCommonly: ['landing_gear_components', 'flight_controls', 'interior_components', 'structural_repairs'],
    },
    {
      id: 'component_overhaul',
      name: 'Component Overhaul',
      description: 'Complete teardown and rebuild of major components',
      category: 'condition_based',
      laborRequirements: [
        { role: 'Overhaul Technician', count: 4, hourlyRate: 75 },
        { role: 'Inspector', count: 1, hourlyRate: 80 },
        { role: 'Engineer', count: 1, hourlyRate: 100 },
      ],
      estimatedDuration: { min: 5, typical: 10, max: 20, unit: 'days' },
      skillsRequired: ['component_overhaul', 'ndt', 'precision_measurement', 'documentation'],
      partsCommonly: ['bearings', 'seals', 'gears', 'shafts', 'housings'],
    },
  ],

  riskFactors: [
    { id: 'safety', name: 'Safety Risk', description: 'Risk to flight safety and passenger/crew safety', category: 'safety', weight: 0.35, calculationMethod: 'exponential', inputs: ['system_health', 'failure_history', 'component_age'] },
    { id: 'dispatch_reliability', name: 'Dispatch Reliability Risk', description: 'Risk of flight delays or cancellations', category: 'operational', weight: 0.30, calculationMethod: 'linear', inputs: ['mel_items', 'maintenance_due', 'parts_availability'] },
    { id: 'regulatory', name: 'Regulatory Risk', description: 'Risk of regulatory non-compliance', category: 'compliance', weight: 0.20, calculationMethod: 'threshold', inputs: ['ad_compliance', 'inspection_status', 'documentation'] },
    { id: 'cost', name: 'Cost Risk', description: 'Risk of unexpected maintenance costs', category: 'financial', weight: 0.15, calculationMethod: 'linear', inputs: ['component_condition', 'warranty_status', 'time_since_overhaul'] },
  ],

  complianceStandards: [
    {
      id: 'faa_part_121',
      name: 'FAA Part 121 Air Carrier Operations',
      shortName: 'FAA Part 121',
      description: 'Federal Aviation Administration regulations for commercial air carriers',
      authority: 'Federal Aviation Administration',
      region: ['US'],
      requirements: [
        { id: 'continuous_airworthiness', name: 'Continuous Airworthiness', description: 'Maintain aircraft in airworthy condition', kpiId: 'ad_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'maintenance_program', name: 'Approved Maintenance Program', description: 'Follow approved maintenance schedule', kpiId: 'scheduled_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'record_keeping', name: 'Maintenance Records', description: 'Complete and accurate maintenance documentation', kpiId: 'documentation_complete', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'monthly',
    },
    {
      id: 'faa_part_145',
      name: 'FAA Part 145 Repair Station',
      shortName: 'FAA Part 145',
      description: 'Federal Aviation Administration regulations for repair stations',
      authority: 'Federal Aviation Administration',
      region: ['US'],
      requirements: [
        { id: 'approved_procedures', name: 'Approved Procedures', description: 'Use only approved data and procedures', kpiId: 'procedure_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'qualified_personnel', name: 'Qualified Personnel', description: 'Only authorized personnel perform maintenance', kpiId: 'training_current', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'inspection_system', name: 'Inspection System', description: 'Maintain required inspection system', kpiId: 'inspection_compliance', threshold: 100, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'quarterly',
    },
    {
      id: 'easa_part_m',
      name: 'EASA Part M Continuing Airworthiness',
      shortName: 'EASA Part M',
      description: 'European Union Aviation Safety Agency continuing airworthiness requirements',
      authority: 'EASA',
      region: ['EU'],
      requirements: [
        { id: 'airworthiness_review', name: 'Airworthiness Review', description: 'Annual airworthiness review completed', kpiId: 'arc_current', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'defect_management', name: 'Defect Management', description: 'Proper handling of defects and deferrals', kpiId: 'defect_closure_rate', threshold: 95, operator: 'gte', severity: 'required' },
        { id: 'camo_compliance', name: 'CAMO Compliance', description: 'Continuing Airworthiness Management Organization compliance', kpiId: 'camo_audit_findings', threshold: 0, operator: 'eq', severity: 'critical' },
      ],
      reportingFrequency: 'monthly',
    },
    {
      id: 'as9100',
      name: 'AS9100 Quality Management',
      shortName: 'AS9100',
      description: 'Aerospace quality management system standard',
      authority: 'IAQG',
      region: ['Global'],
      requirements: [
        { id: 'quality_system', name: 'Quality Management System', description: 'Effective QMS implementation', kpiId: 'qms_audit_score', threshold: 85, operator: 'gte', severity: 'required' },
        { id: 'process_control', name: 'Process Control', description: 'Controlled maintenance processes', kpiId: 'process_deviation_rate', threshold: 2, operator: 'lte', severity: 'required' },
        { id: 'supplier_management', name: 'Supplier Management', description: 'Approved supplier control', kpiId: 'supplier_compliance', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'quarterly',
    },
    {
      id: 'iosa',
      name: 'IATA Operational Safety Audit',
      shortName: 'IOSA',
      description: 'IATA Operational Safety Audit program for airlines',
      authority: 'IATA',
      region: ['Global'],
      requirements: [
        { id: 'maintenance_organization', name: 'Maintenance Organization', description: 'Compliant MRO organization', kpiId: 'iosa_maint_score', threshold: 100, operator: 'eq', severity: 'critical' },
        { id: 'engineering_management', name: 'Engineering Management', description: 'Effective engineering oversight', kpiId: 'engineering_findings', threshold: 0, operator: 'eq', severity: 'required' },
        { id: 'cabin_safety', name: 'Cabin Safety', description: 'Cabin equipment maintenance compliance', kpiId: 'cabin_equipment_status', threshold: 100, operator: 'eq', severity: 'required' },
      ],
      reportingFrequency: 'annual',
    },
  ],

  visualization: {
    primaryColor: '#1e3a5f',
    secondaryColor: '#4a6fa5',
    healthyColor: '#10b981',
    warningColor: '#f59e0b',
    criticalColor: '#ef4444',
    chartDefaults: {
      showGrid: true,
      showLegend: true,
      animationDuration: 300,
    },
  },

  narrativeTemplates: [
    {
      id: 'health_summary',
      name: 'Airworthiness Summary',
      context: 'asset_health',
      audience: ['executive', 'manager', 'engineer'],
      template: 'Your {assetType} "{assetName}" is currently {healthStatus}. {trendDescription} Over the past {timeRange}, {kpiSummary}. {recommendationText}',
      variables: [
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'healthStatus', type: 'terminology' },
        { name: 'trendDescription', type: 'metric' },
        { name: 'timeRange', type: 'time' },
        { name: 'kpiSummary', type: 'metric' },
        { name: 'recommendationText', type: 'metric' },
      ],
    },
    {
      id: 'prediction_alert',
      name: 'Prognostics Alert',
      context: 'prediction',
      audience: ['manager', 'engineer', 'technician'],
      template: '{prediction} for {assetType} "{assetName}": {predictionDetails}. Confidence: {confidence}%. Based on analysis of {dataPoints} flight cycles. Recommended MRO action: {recommendation}',
      variables: [
        { name: 'prediction', type: 'terminology' },
        { name: 'assetType', type: 'terminology' },
        { name: 'assetName', type: 'asset' },
        { name: 'predictionDetails', type: 'metric' },
        { name: 'confidence', type: 'percentage' },
        { name: 'dataPoints', type: 'metric' },
        { name: 'recommendation', type: 'metric' },
      ],
    },
  ],

  features: {
    enableBenchmarking: true,
    enableWhatIf: true,
    enableCollaboration: true,
    enableCompliance: true,
    enableKnowledgeCapture: true,
  },

  defaults: {
    timeRange: '30d',
    refreshInterval: 60,
    defaultDashboard: 'command-center',
  },

  // =============================================================================
  // PROFESSIONAL ROLES (Aerospace-Specific)
  // =============================================================================
  professionalRoles: [
    // SME Roles
    {
      id: 'aircraft_engineer',
      category: 'sme',
      name: 'Aircraft Engineer',
      shortName: 'AE',
      description: 'Licensed engineer responsible for aircraft airworthiness and technical decisions',
      responsibilities: [
        'Airworthiness determination and release',
        'Engineering order development',
        'Technical troubleshooting support',
        'Modification and repair design',
        'Reliability program management',
      ],
      focusAreas: ['airworthiness', 'reliability_metrics', 'technical_records', 'engineering_orders', 'modifications'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: true,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'risk', 'compliance'],
      },
      decisionAuthority: ['airworthiness_release', 'engineering_orders', 'modification_approval'],
      reportingTo: 'Chief Engineer',
      icon: 'GraduationCap',
    },
    // Technician Roles
    {
      id: 'avionics_technician',
      category: 'technician',
      name: 'Avionics Technician',
      shortName: 'AT',
      description: 'Specializes in aircraft electronic and navigation systems maintenance',
      responsibilities: [
        'Avionics system troubleshooting',
        'LRU replacement and testing',
        'Software and database updates',
        'Wiring and connector repairs',
        'Antenna and sensor maintenance',
      ],
      focusAreas: ['avionics_faults', 'bit_failures', 'wiring_issues', 'software_status', 'sensor_calibration'],
      explanationStyle: {
        depth: 'technical',
        includeMetrics: true,
        includeCosts: false,
        includeRootCause: true,
        includeProcedures: true,
        includeTimelines: false,
        vocabularyLevel: 'technical',
        focusOn: ['technical_details', 'action_items'],
      },
      decisionAuthority: ['lru_replacement', 'troubleshooting_path', 'test_procedures'],
      reportingTo: 'Lead Mechanic',
      icon: 'Radio',
    },
    // Executive Roles
    {
      id: 'maintenance_director',
      category: 'executive',
      name: 'Director of Maintenance',
      shortName: 'DOM',
      description: 'Senior executive responsible for all maintenance operations and regulatory compliance',
      responsibilities: [
        'Overall MRO performance',
        'Regulatory compliance oversight',
        'Budget and resource allocation',
        'Safety management system',
        'Vendor and supplier management',
      ],
      focusAreas: ['dispatch_reliability', 'cost_per_flight_hour', 'safety_metrics', 'audit_findings', 'fleet_availability'],
      explanationStyle: {
        depth: 'high_level',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'executive',
        focusOn: ['business_impact', 'risk', 'compliance'],
      },
      decisionAuthority: ['major_repairs', 'budget_allocation', 'regulatory_responses', 'strategic_initiatives'],
      reportingTo: 'VP Operations',
      icon: 'Building',
    },
    // Analyst Roles
    {
      id: 'reliability_analyst',
      category: 'analyst',
      name: 'Reliability Analyst',
      shortName: 'RA',
      description: 'Analyzes aircraft reliability data to identify trends and improvement opportunities',
      responsibilities: [
        'Reliability data analysis',
        'MSG-3 analysis support',
        'Trend monitoring and reporting',
        'PIREP analysis',
        'Maintenance program optimization',
      ],
      focusAreas: ['mtbur', 'mtbf', 'removal_rates', 'top_squawks', 'trend_analysis'],
      explanationStyle: {
        depth: 'detailed',
        includeMetrics: true,
        includeCosts: true,
        includeRootCause: false,
        includeProcedures: false,
        includeTimelines: true,
        vocabularyLevel: 'professional',
        focusOn: ['technical_details', 'business_impact'],
      },
      decisionAuthority: ['analysis_priorities', 'report_formats', 'data_collection_methods'],
      reportingTo: 'Chief Engineer',
      icon: 'BarChart',
    },
  ],

  // =============================================================================
  // PARTS CATALOG (Aerospace Components)
  // =============================================================================
  partsCatalog: {
    categories: [
      {
        id: 'rotables',
        name: 'Rotables',
        description: 'Repairable components with time-controlled limits',
        parts: [
          {
            id: 'engine_hpt_blade',
            partNumber: 'CFM56-5B-HPT-001',
            name: 'HPT Blade Set',
            description: 'High pressure turbine blade set for CFM56-5B engine',
            category: 'rotables',
            manufacturer: 'CFM International',
            unitCost: 125000.00,
            currency: 'USD',
            leadTime: { value: 45, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['engine'],
            failureModes: ['blade_erosion', 'thermal_fatigue', 'foreign_object_damage'],
            averageLifespan: { value: 15000, unit: 'cycles' },
            specifications: { material: 'Single Crystal Nickel Alloy', stages: '1', coating: 'TBC' },
          },
          {
            id: 'landing_gear_actuator',
            partNumber: 'LG-ACT-3200-A',
            name: 'Main Landing Gear Actuator',
            description: 'Hydraulic actuator for main landing gear retraction/extension',
            category: 'rotables',
            manufacturer: 'Safran Landing Systems',
            unitCost: 45000.00,
            currency: 'USD',
            leadTime: { value: 30, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['landing_gear'],
            failureModes: ['seal_leak', 'actuator_drift', 'hydraulic_failure'],
            averageLifespan: { value: 20000, unit: 'cycles' },
            specifications: { pressure: '3000 psi', stroke: '24 inches', type: 'Double Acting' },
          },
          {
            id: 'fuel_boost_pump',
            partNumber: 'FBP-2100-X',
            name: 'Fuel Boost Pump',
            description: 'Electrically driven fuel boost pump for engine feed',
            category: 'rotables',
            manufacturer: 'Parker Aerospace',
            unitCost: 18500.00,
            currency: 'USD',
            leadTime: { value: 21, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['fuel_system'],
            failureModes: ['motor_failure', 'impeller_wear', 'seal_leak'],
            averageLifespan: { value: 12000, unit: 'hours' },
            specifications: { flowRate: '150 GPM', pressure: '45 psi', voltage: '115V AC' },
          },
          {
            id: 'hydraulic_pump',
            partNumber: 'HYD-EDP-5500',
            name: 'Engine Driven Hydraulic Pump',
            description: 'Variable displacement hydraulic pump driven by engine gearbox',
            category: 'rotables',
            manufacturer: 'Eaton Aerospace',
            unitCost: 52000.00,
            currency: 'USD',
            leadTime: { value: 28, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['hydraulic_system'],
            failureModes: ['pump_wear', 'seal_failure', 'cavitation'],
            averageLifespan: { value: 10000, unit: 'hours' },
            specifications: { flowRate: '30 GPM', pressure: '3000 psi', displacement: 'Variable' },
          },
          {
            id: 'apu_starter_generator',
            partNumber: 'APU-SG-131-9A',
            name: 'APU Starter Generator',
            description: 'Combined starter and generator for APU',
            category: 'rotables',
            manufacturer: 'Honeywell',
            unitCost: 35000.00,
            currency: 'USD',
            leadTime: { value: 35, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['apu'],
            failureModes: ['bearing_failure', 'winding_short', 'brush_wear'],
            averageLifespan: { value: 8000, unit: 'hours' },
            specifications: { power: '40 kVA', voltage: '115/200V AC', rpm: '40000' },
          },
        ],
      },
      {
        id: 'consumables',
        name: 'Consumables',
        description: 'Single-use items replaced during maintenance',
        parts: [
          {
            id: 'engine_oil',
            partNumber: 'MIL-PRF-23699-STD',
            name: 'Turbine Engine Oil Type II',
            description: 'Synthetic turbine engine lubricating oil',
            category: 'consumables',
            manufacturer: 'ExxonMobil',
            unitCost: 45.00,
            currency: 'USD',
            leadTime: { value: 1, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['engine', 'apu'],
            failureModes: ['lubrication_failure'],
            specifications: { viscosity: '5 cSt @ 100C', type: 'Synthetic Ester', volume: '1 qt' },
          },
          {
            id: 'hydraulic_fluid',
            partNumber: 'MIL-PRF-83282D',
            name: 'Hydraulic Fluid Type V',
            description: 'Fire-resistant phosphate ester hydraulic fluid',
            category: 'consumables',
            manufacturer: 'Skydrol',
            unitCost: 125.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['hydraulic_system', 'landing_gear'],
            failureModes: ['hydraulic_system_failure'],
            specifications: { viscosity: '10.8 cSt @ 40C', flashPoint: '360F', volume: '1 gal' },
          },
          {
            id: 'o_ring_kit',
            partNumber: 'AS568-KIT-200',
            name: 'O-Ring Assortment Kit',
            description: 'Aircraft grade O-ring assortment for hydraulic systems',
            category: 'consumables',
            manufacturer: 'Parker Seals',
            unitCost: 850.00,
            currency: 'USD',
            leadTime: { value: 3, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['hydraulic_system', 'fuel_system', 'landing_gear'],
            failureModes: ['seal_leak'],
            specifications: { material: 'Fluorocarbon', sizes: '200 sizes', quantity: '1000 pcs' },
          },
        ],
      },
      {
        id: 'expendables',
        name: 'Expendables',
        description: 'Low-value items replaced as needed',
        parts: [
          {
            id: 'safety_wire',
            partNumber: 'MS20995C32',
            name: 'Safety Wire 0.032"',
            description: 'Stainless steel safety lockwire',
            category: 'expendables',
            manufacturer: 'Malin Company',
            unitCost: 15.00,
            currency: 'USD',
            leadTime: { value: 1, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['engine', 'landing_gear', 'hydraulic_system', 'fuel_system', 'apu'],
            failureModes: ['fastener_loosening'],
            specifications: { diameter: '0.032 in', material: 'SS 302/304', weight: '1 lb' },
          },
          {
            id: 'cotter_pin_kit',
            partNumber: 'AN380-KIT',
            name: 'Cotter Pin Assortment',
            description: 'Cadmium plated steel cotter pins assortment',
            category: 'expendables',
            manufacturer: 'MS Hardware',
            unitCost: 45.00,
            currency: 'USD',
            leadTime: { value: 1, unit: 'days' },
            criticalityLevel: 'standard',
            compatibleAssetTypes: ['landing_gear', 'flight_controls'],
            failureModes: ['fastener_failure'],
            specifications: { material: 'Steel Cadmium Plated', sizes: '12 sizes', quantity: '500 pcs' },
          },
          {
            id: 'aircraft_sealant',
            partNumber: 'PR-1422-B2',
            name: 'Fuel Tank Sealant Class B',
            description: 'Polysulfide fuel tank sealant',
            category: 'expendables',
            manufacturer: 'PPG Aerospace',
            unitCost: 185.00,
            currency: 'USD',
            leadTime: { value: 2, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['fuel_system'],
            failureModes: ['fuel_leak'],
            specifications: { cureTime: '24 hrs', application: 'Brush/Faying', shelfLife: '6 months' },
          },
        ],
      },
      {
        id: 'repairable',
        name: 'Repairable',
        description: 'Components that can be repaired and returned to service',
        parts: [
          {
            id: 'fmc_computer',
            partNumber: 'FMC-900-A320',
            name: 'Flight Management Computer',
            description: 'Primary flight management computer for navigation and performance',
            category: 'repairable',
            manufacturer: 'Honeywell',
            unitCost: 95000.00,
            currency: 'USD',
            leadTime: { value: 14, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['avionics'],
            failureModes: ['software_fault', 'processor_failure', 'memory_corruption'],
            averageLifespan: { value: 25000, unit: 'hours' },
            specifications: { channels: 'Dual', database: 'ARINC 424', navCapability: 'RNP AR 0.1' },
          },
          {
            id: 'weather_radar',
            partNumber: 'WXR-2100-MULTI',
            name: 'Weather Radar Transceiver',
            description: 'Multi-scan weather radar with turbulence detection',
            category: 'repairable',
            manufacturer: 'Collins Aerospace',
            unitCost: 45000.00,
            currency: 'USD',
            leadTime: { value: 21, unit: 'days' },
            criticalityLevel: 'essential',
            compatibleAssetTypes: ['avionics'],
            failureModes: ['transmitter_failure', 'receiver_degradation', 'antenna_fault'],
            averageLifespan: { value: 15000, unit: 'hours' },
            specifications: { range: '320 nm', power: '150W', modes: 'WX/TURB/MAP' },
          },
          {
            id: 'brake_assembly',
            partNumber: 'BRK-ASSY-737-MAIN',
            name: 'Main Wheel Brake Assembly',
            description: 'Carbon brake assembly for main landing gear',
            category: 'repairable',
            manufacturer: 'Safran Landing Systems',
            unitCost: 28000.00,
            currency: 'USD',
            leadTime: { value: 10, unit: 'days' },
            criticalityLevel: 'critical',
            compatibleAssetTypes: ['landing_gear'],
            failureModes: ['brake_wear', 'hydraulic_leak', 'overheating'],
            averageLifespan: { value: 2000, unit: 'cycles' },
            specifications: { type: 'Carbon/Carbon', pistons: '5', torque: '850000 in-lb' },
          },
        ],
      },
    ],
  },

  // =============================================================================
  // ROOT CAUSE INTELLIGENCE (Aerospace)
  // =============================================================================
  rootCauseLibrary: {
    categories: [
      {
        id: 'wear',
        name: 'Wear',
        description: 'Root causes related to normal and abnormal component wear',
        icon: 'RefreshCw',
        color: '#3b82f6',
        rootCauses: [
          {
            id: 'bearing_wear',
            name: 'Bearing Wear',
            description: 'Progressive deterioration of bearing surfaces in rotating components',
            category: 'wear',
            symptoms: [
              'Increased vibration levels',
              'Elevated oil debris readings',
              'Higher operating temperatures',
              'Audible noise changes',
              'Performance degradation',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Review oil analysis reports for metal content trends',
                expectedResult: 'Elevated Fe, Cr, or Ni particles indicate bearing wear',
                tools: ['Oil analysis report', 'SOAP trends'],
                duration: 15,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Perform vibration analysis during engine run',
                expectedResult: 'Elevated vibration at bearing frequencies',
                tools: ['Vibration analyzer', 'Engine monitoring data'],
                duration: 30,
                skillLevel: 'advanced',
              },
              {
                order: 3,
                action: 'Borescope inspection if accessible',
                expectedResult: 'Visual evidence of bearing cage or race damage',
                tools: ['Borescope', 'Inspection mirror'],
                duration: 45,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Follow oil change intervals',
              'Monitor oil debris trends',
              'Implement vibration monitoring program',
              'Adhere to time/cycle limits',
              'Use approved lubricants only',
            ],
            typicalProbability: 0.18,
            affectedComponents: ['bearings', 'shafts', 'gearboxes', 'generators'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 8, typical: 24, max: 72, unit: 'hours' },
            costRange: { min: 5000, max: 150000, currency: 'USD' },
            industryReferences: ['SAE ARP1587', 'OEM Maintenance Manual'],
          },
          {
            id: 'brake_wear',
            name: 'Brake Wear',
            description: 'Normal or accelerated wear of carbon or steel brake components',
            category: 'wear',
            symptoms: [
              'Brake wear indicator approaching limits',
              'Extended stopping distance',
              'Brake temperature exceedances',
              'Uneven brake application',
              'Increased brake maintenance frequency',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Measure brake wear indicator pins',
                expectedResult: 'Wear within limits per maintenance manual',
                tools: ['Brake wear indicator gauge', 'Ruler'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Inspect brake stack condition visually',
                expectedResult: 'No excessive glazing, cracking, or uneven wear',
                tools: ['Flashlight', 'Mirror'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Review brake temperature data from FDR',
                expectedResult: 'Normal temperature profiles during braking',
                tools: ['FDR readout', 'Temperature trends'],
                duration: 20,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Monitor brake wear trends',
              'Review pilot braking technique data',
              'Ensure proper brake cooling procedures',
              'Follow autobrake usage guidelines',
              'Rotate tires to equalize wear',
            ],
            typicalProbability: 0.22,
            affectedComponents: ['brake_discs', 'brake_linings', 'brake_pistons'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 2, typical: 4, max: 8, unit: 'hours' },
            costRange: { min: 2000, max: 35000, currency: 'USD' },
            industryReferences: ['Aircraft Maintenance Manual', 'OEM Service Bulletin'],
          },
        ],
      },
      {
        id: 'corrosion',
        name: 'Corrosion',
        description: 'Root causes related to material degradation from corrosion',
        icon: 'Droplets',
        color: '#f59e0b',
        rootCauses: [
          {
            id: 'galvanic_corrosion',
            name: 'Galvanic Corrosion',
            description: 'Electrochemical corrosion between dissimilar metals',
            category: 'corrosion',
            symptoms: [
              'White or green deposits at fastener locations',
              'Pitting on aluminum surfaces',
              'Degraded paint or primer',
              'Swelling around fasteners',
              'Electrical bonding resistance increase',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Visual inspection for corrosion deposits',
                expectedResult: 'Identify location and extent of corrosion',
                tools: ['Flashlight', 'Magnifying glass', 'Mirror'],
                duration: 30,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'NDT inspection if required',
                expectedResult: 'Determine depth and extent of material loss',
                tools: ['Eddy current tester', 'Ultrasonic thickness gauge'],
                duration: 60,
                skillLevel: 'advanced',
              },
              {
                order: 3,
                action: 'Review maintenance history for previous corrosion findings',
                expectedResult: 'Identify recurring corrosion areas',
                tools: ['Maintenance records', 'Corrosion log'],
                duration: 20,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Apply corrosion preventive compounds',
              'Ensure proper sealant application',
              'Maintain paint and primer integrity',
              'Follow washing and cleaning procedures',
              'Implement corrosion prevention control program',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['fuselage', 'wing_structure', 'landing_gear', 'fasteners'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 4, typical: 16, max: 80, unit: 'hours' },
            costRange: { min: 500, max: 50000, currency: 'USD' },
            industryReferences: ['AC 43-4B', 'CPCP Manual'],
          },
          {
            id: 'stress_corrosion',
            name: 'Stress Corrosion Cracking',
            description: 'Crack formation due to combined tensile stress and corrosive environment',
            category: 'corrosion',
            symptoms: [
              'Hairline cracks at high-stress areas',
              'Intergranular cracking patterns',
              'Sudden structural failure potential',
              'Located near fastener holes',
              'Associated with residual manufacturing stress',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Visual and dye penetrant inspection',
                expectedResult: 'Identify surface-breaking cracks',
                tools: ['Dye penetrant kit', 'UV light', 'Magnifying glass'],
                duration: 45,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Eddy current inspection of susceptible areas',
                expectedResult: 'Detect subsurface cracking',
                tools: ['Eddy current equipment', 'Calibration standards'],
                duration: 60,
                skillLevel: 'advanced',
              },
              {
                order: 3,
                action: 'Ultrasonic inspection if crack detected',
                expectedResult: 'Determine crack depth and orientation',
                tools: ['Ultrasonic flaw detector', 'Angle beam probes'],
                duration: 45,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'Shot peening of susceptible areas',
              'Proper heat treatment verification',
              'Stress relief after cold working',
              'Controlled environment storage',
              'Periodic NDT inspection program',
            ],
            typicalProbability: 0.08,
            affectedComponents: ['wing_spar', 'fuselage_frames', 'landing_gear_components'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 8, typical: 40, max: 160, unit: 'hours' },
            costRange: { min: 5000, max: 200000, currency: 'USD' },
            industryReferences: ['AC 43-4B', 'SRM', 'ASTM E1681'],
          },
        ],
      },
      {
        id: 'fatigue',
        name: 'Fatigue',
        description: 'Root causes related to cyclic loading and fatigue damage',
        icon: 'Activity',
        color: '#10b981',
        rootCauses: [
          {
            id: 'metal_fatigue',
            name: 'Metal Fatigue',
            description: 'Progressive structural damage from cyclic loading',
            category: 'fatigue',
            symptoms: [
              'Fatigue cracks at stress concentrations',
              'Beach marks on fracture surfaces',
              'Crack propagation from rivet holes',
              'Skin panel cracking',
              'Spar cap cracks',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Visual inspection at known fatigue locations',
                expectedResult: 'Identify visible cracks or crack indications',
                tools: ['Flashlight', 'Magnifying glass', '10x loupe'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 2,
                action: 'Eddy current or dye penetrant inspection',
                expectedResult: 'Detect fatigue cracks not visible to naked eye',
                tools: ['Eddy current equipment', 'Dye penetrant kit'],
                duration: 60,
                skillLevel: 'advanced',
              },
              {
                order: 3,
                action: 'Review flight cycles and hard landing history',
                expectedResult: 'Correlate damage with loading events',
                tools: ['Maintenance records', 'FDR data'],
                duration: 30,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Adhere to structural inspection program',
              'Monitor flight cycles and landings',
              'Apply cold working to critical holes',
              'Replace time-limited components',
              'Track and repair minor damage promptly',
            ],
            typicalProbability: 0.12,
            affectedComponents: ['wing_skins', 'fuselage_skins', 'spar_caps', 'pressure_bulkhead'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 16, typical: 80, max: 400, unit: 'hours' },
            costRange: { min: 10000, max: 500000, currency: 'USD' },
            industryReferences: ['SRM', 'Damage Tolerance Analysis', 'AD compliance'],
          },
          {
            id: 'thermal_fatigue',
            name: 'Thermal Fatigue',
            description: 'Damage from repeated thermal cycling in hot section components',
            category: 'fatigue',
            symptoms: [
              'Thermal barrier coating spallation',
              'Blade tip oxidation and erosion',
              'Cracking at cooling hole edges',
              'Creep deformation',
              'EGT margin deterioration',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Borescope inspection of hot section',
                expectedResult: 'Visual evidence of thermal distress',
                tools: ['Borescope', 'Documentation camera'],
                duration: 45,
                skillLevel: 'advanced',
              },
              {
                order: 2,
                action: 'Review EGT trends and exceedances',
                expectedResult: 'Identify thermal cycles and overtemperature events',
                tools: ['Engine trend monitoring data', 'ECU data'],
                duration: 30,
                skillLevel: 'intermediate',
              },
              {
                order: 3,
                action: 'Dimensional inspection if removed',
                expectedResult: 'Measure creep and erosion against limits',
                tools: ['CMM', 'Precision gauges'],
                duration: 120,
                skillLevel: 'expert',
              },
            ],
            preventiveActions: [
              'Monitor EGT margins closely',
              'Avoid rapid throttle movements',
              'Proper engine starting procedures',
              'Trend analysis for early detection',
              'Follow manufacturer thermal limits',
            ],
            typicalProbability: 0.14,
            affectedComponents: ['turbine_blades', 'turbine_nozzles', 'combustion_liners', 'shrouds'],
            requiredExpertise: ['sme'],
            estimatedResolutionTime: { min: 24, typical: 120, max: 480, unit: 'hours' },
            costRange: { min: 50000, max: 800000, currency: 'USD' },
            industryReferences: ['OEM Maintenance Manual', 'Engine Service Bulletins'],
          },
        ],
      },
      {
        id: 'foreign_object_damage',
        name: 'Foreign Object Damage',
        description: 'Root causes related to FOD events',
        icon: 'AlertTriangle',
        color: '#ef4444',
        rootCauses: [
          {
            id: 'engine_fod',
            name: 'Engine FOD',
            description: 'Foreign object damage to engine components from ingestion',
            category: 'foreign_object_damage',
            symptoms: [
              'Fan blade nicks and dents',
              'Compressor blade damage',
              'EGT increase',
              'Vibration increase',
              'Engine stall or surge',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Inlet and fan blade visual inspection',
                expectedResult: 'Identify FOD damage on accessible blades',
                tools: ['Flashlight', 'Blade inspection mirror'],
                duration: 20,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Borescope inspection of compressor stages',
                expectedResult: 'Assess damage extent in inaccessible areas',
                tools: ['Borescope', 'Documentation system'],
                duration: 60,
                skillLevel: 'advanced',
              },
              {
                order: 3,
                action: 'Engine ground run with vibration monitoring',
                expectedResult: 'Verify safe operation within limits',
                tools: ['Vibration monitoring equipment', 'Engine run checklist'],
                duration: 45,
                skillLevel: 'advanced',
              },
            ],
            preventiveActions: [
              'FOD walk-downs before operations',
              'Inlet covers when parked',
              'Tool accountability program',
              'Debris containment procedures',
              'Regular ramp sweeping',
            ],
            typicalProbability: 0.10,
            affectedComponents: ['fan_blades', 'compressor_blades', 'inlet_guide_vanes'],
            requiredExpertise: ['technician', 'sme'],
            estimatedResolutionTime: { min: 4, typical: 24, max: 200, unit: 'hours' },
            costRange: { min: 1000, max: 2000000, currency: 'USD' },
            industryReferences: ['OEM Maintenance Manual', 'AC 150/5210-24'],
          },
          {
            id: 'tire_fod',
            name: 'Tire FOD',
            description: 'Foreign object damage to aircraft tires from runway debris',
            category: 'foreign_object_damage',
            symptoms: [
              'Tire cuts and gouges',
              'Embedded objects in tread',
              'Sidewall damage',
              'Abnormal tire wear',
              'Vibration during taxi',
            ],
            diagnosticSteps: [
              {
                order: 1,
                action: 'Visual inspection of tire surface',
                expectedResult: 'Identify cuts, gouges, or embedded objects',
                tools: ['Flashlight', 'Tire probe'],
                duration: 15,
                skillLevel: 'basic',
              },
              {
                order: 2,
                action: 'Measure cut depth against limits',
                expectedResult: 'Determine if damage is within allowable limits',
                tools: ['Depth gauge', 'Maintenance manual'],
                duration: 10,
                skillLevel: 'basic',
              },
              {
                order: 3,
                action: 'Check for cord exposure or carcass damage',
                expectedResult: 'Assess structural integrity of tire',
                tools: ['Inspection mirror', 'Magnifying glass'],
                duration: 10,
                skillLevel: 'intermediate',
              },
            ],
            preventiveActions: [
              'Regular runway inspection',
              'FOD reporting program',
              'Proper taxi procedures',
              'Pre-flight tire inspection',
              'Maintain tire pressures',
            ],
            typicalProbability: 0.15,
            affectedComponents: ['main_tires', 'nose_tires'],
            requiredExpertise: ['technician'],
            estimatedResolutionTime: { min: 30, typical: 60, max: 120, unit: 'minutes' },
            costRange: { min: 500, max: 3000, currency: 'USD' },
            industryReferences: ['Tire manufacturer manual', 'Aircraft Maintenance Manual'],
          },
        ],
      },
    ],
    commonPatterns: [
      {
        id: 'engine_degradation',
        name: 'Engine Performance Degradation Cascade',
        description: 'Progressive engine deterioration from initial wear to performance loss',
        sequence: [
          'Initial blade tip erosion from normal operation',
          'Compressor efficiency decrease',
          'EGT margin reduction',
          'Increased fuel consumption',
          'Performance limiting EGT reached',
          'Engine removal required',
        ],
        indicators: [
          'EGT margin trending downward',
          'Fuel flow increasing for same thrust',
          'N1/N2 requiring higher values',
        ],
        preventionStrategy: 'Implement engine condition monitoring with trend alerts for early performance degradation detection',
      },
      {
        id: 'hydraulic_contamination',
        name: 'Hydraulic System Contamination Cascade',
        description: 'Progressive system degradation from fluid contamination',
        sequence: [
          'Contamination enters system',
          'Filter differential pressure increases',
          'Pump wear accelerates',
          'Servo valve scoring begins',
          'Actuator seal degradation',
          'System pressure fluctuations',
          'Flight control anomalies',
        ],
        indicators: [
          'Filter bypass indication',
          'Fluid analysis showing elevated particle count',
          'Servo valve hunting or slow response',
        ],
        preventionStrategy: 'Regular fluid sampling and analysis with immediate filter replacement at first indication of bypass',
      },
    ],
  },

  // =============================================================================
  // LABOR RATES (Aerospace Industry)
  // =============================================================================
  laborRates: {
    roles: [
      { roleId: 'ap_mechanic', name: 'A&P Mechanic', hourlyRate: 65, currency: 'USD', certifications: ['FAA A&P License', 'Type Rating'] },
      { roleId: 'avionics_technician', name: 'Avionics Technician', hourlyRate: 75, currency: 'USD', certifications: ['FCC License', 'Avionics Type Rating'] },
      { roleId: 'lead_mechanic', name: 'Lead Mechanic', hourlyRate: 80, currency: 'USD', certifications: ['FAA A&P License', 'IA Certification'] },
      { roleId: 'inspector', name: 'Inspector', hourlyRate: 80, currency: 'USD', certifications: ['FAA IA', 'NDT Level II'] },
      { roleId: 'structures_specialist', name: 'Structures Specialist', hourlyRate: 85, currency: 'USD', certifications: ['Composites Repair', 'Sheet Metal'] },
      { roleId: 'aircraft_engineer', name: 'Aircraft Engineer', hourlyRate: 100, currency: 'USD', certifications: ['PE License', 'DER/DAR'] },
      { roleId: 'ndt_technician', name: 'NDT Technician', hourlyRate: 90, currency: 'USD', certifications: ['ASNT Level II/III', 'NAS-410'] },
      { roleId: 'contract_mechanic', name: 'Contract A&P Mechanic', hourlyRate: 85, currency: 'USD', certifications: ['FAA A&P License'] },
    ],
    overtimeMultiplier: 1.5,
    emergencyMultiplier: 2.0,
    weekendMultiplier: 1.5,
  },
}
